import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Select } from '../ui';
import { IngredientDetail, Recipe, RecipeIngredient, UserIngredient, UserInventory, UserRecipe } from '../../types';
import { calculateRecipeNutrition, dataService } from '@foodbook/core-logic';

const DEFAULT_RECIPE_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 720 720">
    <rect width="720" height="720" fill="#f6e7cb"/>
    <rect x="36" y="36" width="648" height="648" rx="36" fill="#fff8e8" stroke="#8b4513" stroke-width="12" stroke-dasharray="18 14"/>
    <circle cx="280" cy="280" r="86" fill="#d9b382"/>
    <path d="M164 520c32-88 90-132 176-132 88 0 152 44 196 132" fill="none" stroke="#8b4513" stroke-width="28" stroke-linecap="round"/>
    <path d="M246 258l38 40 82-92" fill="none" stroke="#fff8e8" stroke-width="24" stroke-linecap="round" stroke-linejoin="round"/>
    <text x="360" y="610" text-anchor="middle" font-size="42" font-family="Georgia, serif" fill="#8b4513" font-weight="700">上传食谱封面</text>
  </svg>`
)}`;

const PRESET_TAGS = [
  '川菜', '粤菜', '家常菜', '快手', '辣', '甜', '清淡', '低脂', '高蛋白'
];

type RecipeIngredientDraft = RecipeIngredient & {
  createNewIngredient?: boolean;
  amountValue?: string;
  amountUnit?: 'g' | 'ml';
};

type NewIngredientDraft = {
  name: string;
  tags: string;
  description: string;
};

type IngredientCandidate = IngredientDetail | UserIngredient;
type QuantityUnit = 'g' | 'ml';
type CropState = {
  src: string;
  mimeType: string;
  scale: number;
  offsetX: number;
  offsetY: number;
};

const CENTER_SNAP_THRESHOLD = 12;

interface RecipeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (recipe: Recipe) => void;
  ingredients: IngredientDetail[];
  userInventory: UserInventory;
  editingRecipe?: UserRecipe | null;
}

const normalizeIngredientText = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[()（）[\]【】\-_,.，。:：;'"]/g, '')
    .replace(/\d+$/g, '');
};

const extractNameVariants = (value: string): string[] => {
  const variants = new Set<string>();
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  variants.add(trimmed);

  const withoutBrackets = trimmed.replace(/[\(\[（【].*?[\)\]）】]/g, ' ').trim();
  if (withoutBrackets) {
    variants.add(withoutBrackets);
  }

  const bracketMatches = trimmed.match(/[\(\[（【]([^()\[\]（）【】]+)[\)\]）】]/g) || [];
  for (const rawMatch of bracketMatches) {
    const inner = rawMatch.slice(1, -1).trim();
    if (inner) {
      variants.add(inner);
    }
  }

  const splitMatches = trimmed
    .split(/[\/、,，;；]/)
    .map(part => part.trim())
    .filter(Boolean);
  for (const part of splitMatches) {
    variants.add(part);
  }

  return Array.from(variants);
};

const buildCandidateTexts = (ingredient: IngredientCandidate): string[] => {
  return [
    ...extractNameVariants(ingredient.name),
    ingredient.englishName || '',
    ...(ingredient.aliasNames || []),
    ...(ingredient.tags || []),
  ].flatMap(value => extractNameVariants(value)).filter(Boolean);
};

const getMatchScore = (query: string, ingredient: IngredientCandidate): number => {
  const normalizedQuery = normalizeIngredientText(query);
  if (!normalizedQuery) {
    return 0;
  }

  let score = 0;
  for (const candidate of buildCandidateTexts(ingredient)) {
    const normalizedCandidate = normalizeIngredientText(candidate);
    if (!normalizedCandidate) continue;

    if (normalizedCandidate === normalizedQuery) {
      score = Math.max(score, 100);
    } else if (normalizedCandidate.includes(normalizedQuery) || normalizedQuery.includes(normalizedCandidate)) {
      score = Math.max(score, 80);
    } else if (
      normalizedCandidate.startsWith(normalizedQuery.slice(0, Math.min(3, normalizedQuery.length))) ||
      normalizedQuery.startsWith(normalizedCandidate.slice(0, Math.min(3, normalizedCandidate.length)))
    ) {
      score = Math.max(score, 60);
    }
  }

  return score;
};

const parseDraftAmount = (amount: string): { amountValue: string; amountUnit: QuantityUnit } => {
  const amountValue = amount.match(/(\d+(?:\.\d+)?)/)?.[1] || '';
  const amountUnit = /ml/i.test(amount) ? 'ml' : 'g';
  return { amountValue, amountUnit };
};

const buildAmountString = (value?: string, unit: QuantityUnit = 'g'): string => {
  const trimmed = value?.trim() || '';
  return trimmed ? `${trimmed}${unit}` : '';
};

const snapToCenter = (value: number): number => (Math.abs(value) <= CENTER_SNAP_THRESHOLD ? 0 : value);

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('读取图片失败'));
    reader.readAsDataURL(file);
  });

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('加载图片失败'));
    image.src = src;
  });

const canvasToDataUrl = async (canvas: HTMLCanvasElement, mimeType: string, maxBytes: number): Promise<string> => {
  const preferredMime = mimeType === 'image/png' ? 'image/jpeg' : mimeType;
  let quality = 0.92;
  let lastResult = canvas.toDataURL(preferredMime, quality);

  while (quality >= 0.45) {
    const result = canvas.toDataURL(preferredMime, quality);
    lastResult = result;
    const bytes = Math.ceil((result.length - 'data:image/jpeg;base64,'.length) * 3 / 4);
    if (bytes <= maxBytes) {
      return result;
    }
    quality -= 0.08;
  }

  return lastResult;
};

const createSquareCroppedImage = async (cropState: CropState): Promise<string> => {
  const sourceImage = await loadImageElement(cropState.src);
  const canvas = document.createElement('canvas');
  const size = 720;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('图片裁剪失败');
  }

  const baseScale = Math.max(size / sourceImage.width, size / sourceImage.height);
  const drawWidth = sourceImage.width * baseScale * cropState.scale;
  const drawHeight = sourceImage.height * baseScale * cropState.scale;
  const maxOffsetX = Math.max(0, (drawWidth - size) / 2);
  const maxOffsetY = Math.max(0, (drawHeight - size) / 2);
  const safeOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, cropState.offsetX));
  const safeOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, cropState.offsetY));
  const drawX = (size - drawWidth) / 2 + safeOffsetX;
  const drawY = (size - drawHeight) / 2 + safeOffsetY;

  ctx.fillStyle = '#f8ecd1';
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(sourceImage, drawX, drawY, drawWidth, drawHeight);

  return canvasToDataUrl(canvas, cropState.mimeType, 300 * 1024);
};

export const RecipeFormModal = ({
  isOpen,
  onClose,
  onSave,
  ingredients,
  userInventory,
  editingRecipe
}: RecipeFormModalProps) => {
  const submittingRef = useRef(false);
  const [formData, setFormData] = useState<Partial<Recipe>>({
    name: '',
    time: '',
    difficulty: 3,
    calories: 0,
    image: DEFAULT_RECIPE_IMAGE,
    tags: [],
    description: '',
    ingredients: [],
    steps: [],
    isFavorite: false,
  });
  const [newTag, setNewTag] = useState('');
  const [userIngredients, setUserIngredients] = useState<UserIngredient[]>([]);
  const [newIngredientDrafts, setNewIngredientDrafts] = useState<Record<number, NewIngredientDraft>>({});
  const [activeNewIngredientIndex, setActiveNewIngredientIndex] = useState<number | null>(null);
  const [cropState, setCropState] = useState<CropState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingRecipe) {
      setFormData({
        name: editingRecipe.name,
        time: editingRecipe.time,
        difficulty: editingRecipe.difficulty,
        calories: editingRecipe.calories,
        manualCalories: editingRecipe.manualCalories,
        autoCalories: editingRecipe.autoCalories,
        image: editingRecipe.image || DEFAULT_RECIPE_IMAGE,
        tags: editingRecipe.tags,
        description: editingRecipe.description,
        ingredients: (editingRecipe.ingredients || []).map(ingredient => ({
          ...ingredient,
          ...parseDraftAmount(ingredient.amount),
          createNewIngredient: false,
        })),
        steps: editingRecipe.steps,
        isFavorite: false,
      });
    } else {
      setFormData({
        name: '',
        time: '',
        difficulty: 3,
        calories: 0,
        manualCalories: undefined,
        autoCalories: undefined,
        image: DEFAULT_RECIPE_IMAGE,
        tags: [],
        description: '',
        ingredients: [],
        steps: [],
        isFavorite: false,
      });
    }
    setNewIngredientDrafts({});
    setActiveNewIngredientIndex(null);
    setCropState(null);
  }, [editingRecipe, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    const loadUserIngredients = async () => {
      try {
        const savedUserIngredients = await dataService.getUserIngredients();
        if (!cancelled) {
          setUserIngredients(savedUserIngredients);
        }
      } catch (error) {
        console.warn('[RecipeFormModal] 加载自定义食材失败:', error);
        if (!cancelled) {
          setUserIngredients([]);
        }
      }
    };

    void loadUserIngredients();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const allIngredients = useMemo(() => [...ingredients, ...userIngredients], [ingredients, userIngredients]);
  const nutritionIngredients = useMemo<IngredientDetail[]>(
    () =>
      allIngredients.map((ingredient) => ({
        id: ingredient.id,
        name: ingredient.name,
        aliasNames: ingredient.aliasNames || [],
        englishName: ingredient.englishName || '',
        category: ingredient.category || '未分类',
        subCategory: ingredient.subCategory || '待完善',
        calories: ingredient.calories || 0,
        image: ingredient.image || '',
        description: ingredient.description || '',
        unit: ingredient.unit === 'ml' ? 'ml' : 'g',
        nutrition: ingredient.nutrition || {
          protein: 0,
          fat: 0,
          carbs: 0,
          fiber: 0,
        },
        tags: ingredient.tags || [],
        isPublic: ingredient.isPublic,
        ownerId: ingredient.ownerId,
        createdAt: ingredient.createdAt,
        updatedAt: ingredient.updatedAt,
      })),
    [allIngredients]
  );
  const remainingCustomIngredients = Math.max(0, 100 - userIngredients.length);
  const liveAutoCalories = useMemo(() => {
    const preparedIngredients = (((formData.ingredients as RecipeIngredientDraft[]) || []))
      .filter((ingredient) => ingredient?.name?.trim() && ingredient.amountValue)
      .map((ingredient) => ({
        name: ingredient.name.trim(),
        amount: buildAmountString(ingredient.amountValue, ingredient.amountUnit || 'g'),
        note: ingredient.note?.trim() || undefined,
        linkedIngredientId: ingredient.linkedIngredientId,
      }));

    if (preparedIngredients.length === 0) {
      return undefined;
    }

    const calculatedNutrition = calculateRecipeNutrition({
      id: editingRecipe?.id || 'live-preview',
      name: formData.name?.trim() || '食谱草稿',
      time: formData.time?.trim() || '',
      difficulty: formData.difficulty || 3,
      calories: 0,
      image: formData.image || DEFAULT_RECIPE_IMAGE,
      tags: formData.tags || [],
      description: formData.description?.trim() || '',
      ingredients: preparedIngredients,
      steps: formData.steps?.filter(step => step.trim()) || [],
    }, nutritionIngredients);

    return calculatedNutrition?.calories;
  }, [editingRecipe?.id, formData.description, formData.difficulty, formData.image, formData.ingredients, formData.name, formData.steps, formData.tags, formData.time, nutritionIngredients]);

  if (!isOpen) return null;

  const toRecipe = (recipe: UserRecipe | Recipe): Recipe => ({
    id: recipe.id,
    name: recipe.name,
    time: recipe.time ?? '',
    difficulty: recipe.difficulty ?? 3,
    calories: recipe.calories ?? 0,
    manualCalories: recipe.manualCalories,
    autoCalories: recipe.autoCalories,
    image: recipe.image ?? DEFAULT_RECIPE_IMAGE,
    tags: recipe.tags ?? [],
    description: recipe.description,
    ingredients: recipe.ingredients,
    steps: recipe.steps,
    nutrition: recipe.nutrition,
    ownerId: recipe.ownerId,
    isFavorite: 'isFavorite' in recipe ? recipe.isFavorite : undefined,
  });

  const getIngredientMatches = (query: string): IngredientCandidate[] => {
    return allIngredients
      .map((ingredient) => ({ ingredient, score: getMatchScore(query, ingredient) }))
      .filter(item => item.score > 0)
      .sort((left, right) => right.score - left.score || left.ingredient.name.localeCompare(right.ingredient.name))
      .slice(0, 6)
      .map(item => item.ingredient);
  };

  const getDraft = (index: number, ingredientName = ''): NewIngredientDraft => {
    return newIngredientDrafts[index] || {
      name: ingredientName.trim(),
      tags: '',
      description: '',
    };
  };

  const handleAddIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [
        ...((prev.ingredients as RecipeIngredientDraft[]) || []),
        { name: '', amount: '', amountValue: '', amountUnit: 'g', note: '', linkedIngredientId: undefined, createNewIngredient: false }
      ]
    }));
  };

  const handleRemoveIngredient = (index: number) => {
    setFormData(prev => ({
      ...prev,
      ingredients: ((prev.ingredients as RecipeIngredientDraft[]) || []).filter((_, currentIndex) => currentIndex !== index)
    }));
    setNewIngredientDrafts(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleIngredientChange = (index: number, field: keyof RecipeIngredientDraft, value: string | boolean) => {
    setFormData(prev => {
      const nextIngredients = [...(((prev.ingredients as RecipeIngredientDraft[]) || []))];
      const currentIngredient = nextIngredients[index] || { name: '', amount: '', amountValue: '', amountUnit: 'g' as QuantityUnit };
      const updatedIngredient = { ...currentIngredient, [field]: value };

      if (field === 'amountValue' || field === 'amountUnit') {
        updatedIngredient.amount = buildAmountString(
          field === 'amountValue' ? String(value) : (updatedIngredient.amountValue || ''),
          field === 'amountUnit' ? value as QuantityUnit : (updatedIngredient.amountUnit || 'g')
        );
      }

      if (field === 'name') {
        const matchedIngredient = allIngredients.find(ingredient => ingredient.name === value);
        if (matchedIngredient) {
          updatedIngredient.linkedIngredientId = matchedIngredient.id;
          updatedIngredient.createNewIngredient = false;
        } else {
          delete updatedIngredient.linkedIngredientId;
          updatedIngredient.createNewIngredient = false;
        }
      }

      nextIngredients[index] = updatedIngredient;
      return { ...prev, ingredients: nextIngredients };
    });
  };

  const handleUseExistingIngredient = (index: number, ingredient: IngredientCandidate) => {
    setFormData(prev => {
      const nextIngredients = [...(((prev.ingredients as RecipeIngredientDraft[]) || []))];
      nextIngredients[index] = {
        ...nextIngredients[index],
        name: ingredient.name,
        linkedIngredientId: ingredient.id,
        createNewIngredient: false,
        amountUnit: ingredient.unit === 'ml' ? 'ml' : 'g',
        amount: buildAmountString(nextIngredients[index]?.amountValue, ingredient.unit === 'ml' ? 'ml' : 'g'),
      };
      return { ...prev, ingredients: nextIngredients };
    });
  };

  const handleToggleCreateNewIngredient = (index: number, enabled: boolean) => {
    setFormData(prev => {
      const nextIngredients = [...(((prev.ingredients as RecipeIngredientDraft[]) || []))];
      const currentIngredient = nextIngredients[index];
      if (!currentIngredient) {
        return prev;
      }

      nextIngredients[index] = {
        ...currentIngredient,
        createNewIngredient: enabled,
      };

      if (enabled) {
        delete nextIngredients[index].linkedIngredientId;
      }

      return { ...prev, ingredients: nextIngredients };
    });
    setActiveNewIngredientIndex(enabled ? index : null);
  };

  const handleConfirmNewIngredientDraft = (index: number) => {
    const ingredient = ((formData.ingredients as RecipeIngredientDraft[]) || [])[index];
    const draft = getDraft(index, ingredient?.name || '');
    if (!draft.name.trim()) {
      alert('请填写新食材名称');
      return;
    }
    setNewIngredientDrafts(prev => ({
      ...prev,
      [index]: {
        ...draft,
        name: draft.name.trim(),
      },
    }));
    setActiveNewIngredientIndex(null);
  };

  const handleOpenImagePicker = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: DEFAULT_RECIPE_IMAGE }));
  };

  const handleSelectImageFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件');
      return;
    }

    try {
      const src = await readFileAsDataUrl(file);
      setCropState({
        src,
        mimeType: file.type,
        scale: 1,
        offsetX: 0,
        offsetY: 0,
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : '读取图片失败');
    }
  };

  const handleApplyCroppedImage = async () => {
    if (!cropState) {
      return;
    }

    try {
      const croppedImage = await createSquareCroppedImage(cropState);
      setFormData(prev => ({ ...prev, image: croppedImage }));
      setCropState(null);
    } catch (error) {
      alert(error instanceof Error ? error.message : '处理图片失败');
    }
  };

  const handleDraftChange = (index: number, field: keyof NewIngredientDraft, value: string, ingredientName: string) => {
    setNewIngredientDrafts(prev => ({
      ...prev,
      [index]: {
        ...getDraft(index, ingredientName),
        [field]: value,
      },
    }));
  };

  const handleAddStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...(prev.steps || []), '']
    }));
  };

  const handleRemoveStep = (index: number) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps?.filter((_, currentIndex) => currentIndex !== index)
    }));
  };

  const handleStepChange = (index: number, value: string) => {
    setFormData(prev => {
      const nextSteps = [...(prev.steps || [])];
      nextSteps[index] = value;
      return { ...prev, steps: nextSteps };
    });
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleToggleTag = (tag: string) => {
    if (formData.tags?.includes(tag)) {
      handleRemoveTag(tag);
    } else {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current) {
      return;
    }

    if (!formData.name?.trim()) {
      alert('请填写食谱名称');
      return;
    }
    if (!formData.time?.trim()) {
      alert('请填写烹饪时间');
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const preparedIngredients: RecipeIngredient[] = [];
      const currentIngredients = (formData.ingredients as RecipeIngredientDraft[]) || [];

      for (let index = 0; index < currentIngredients.length; index += 1) {
        const ingredient = currentIngredients[index];
        if (!ingredient?.name?.trim()) {
          continue;
        }

        let linkedIngredientId = ingredient.linkedIngredientId;
        let resolvedName = ingredient.name.trim();

        if (!linkedIngredientId) {
          if (remainingCustomIngredients <= 0) {
            alert('你已达到自定义食材上限（100条），请优先复用已有食材或清理旧词条。');
            return;
          }

          const draft = getDraft(index, resolvedName);
          const createdIngredient = await dataService.createUserIngredient({
            userId: dataService.getUserId(),
            name: draft.name.trim() || resolvedName,
            tags: draft.tags
              .split(/[，,]/)
              .map(tag => tag.trim())
              .filter(Boolean),
            description: draft.description.trim(),
            category: '未分类',
            subCategory: '待完善',
            unit: 'g',
            calories: 0,
            nutrition: {
              calories: 0,
              protein: 0,
              fat: 0,
              carbs: 0,
              fiber: 0,
            },
            isPublic: false,
          });
          linkedIngredientId = createdIngredient.id;
          resolvedName = createdIngredient.name;
          setUserIngredients(prev => [...prev, createdIngredient]);
        }

        preparedIngredients.push({
          name: resolvedName,
          amount: buildAmountString(ingredient.amountValue, ingredient.amountUnit || 'g'),
          note: ingredient.note?.trim() || undefined,
          linkedIngredientId,
        });
      }

      const recipeForCalculation: Recipe = {
        id: editingRecipe?.id || `draft-${Date.now()}`,
        name: formData.name.trim(),
        time: formData.time.trim(),
        difficulty: formData.difficulty || 3,
        calories: 0,
        image: formData.image || DEFAULT_RECIPE_IMAGE,
        tags: formData.tags || [],
        description: formData.description?.trim() || '',
        ingredients: preparedIngredients,
        steps: formData.steps?.filter(step => step.trim()) || [],
      };
      const calculatedNutrition = calculateRecipeNutrition(recipeForCalculation, nutritionIngredients);
      const manualCalories = Number(formData.manualCalories) > 0 ? Number(formData.manualCalories) : undefined;
      const autoCalories = calculatedNutrition?.calories && calculatedNutrition.calories > 0
        ? calculatedNutrition.calories
        : undefined;
      const effectiveCalories = manualCalories ?? autoCalories ?? 0;

      const recipeData = {
        name: formData.name.trim(),
        time: formData.time.trim(),
        difficulty: formData.difficulty || 3,
        calories: effectiveCalories,
        manualCalories,
        autoCalories,
        image: formData.image || DEFAULT_RECIPE_IMAGE,
        tags: formData.tags || [],
        description: formData.description?.trim() || '',
        ingredients: preparedIngredients,
        steps: formData.steps?.filter(step => step.trim()) || [],
        nutrition: calculatedNutrition || undefined,
        isPublic: false,
      };

      let savedRecipe: Recipe;

      if (editingRecipe) {
        await dataService.updateUserRecipe(editingRecipe.id, recipeData);
        savedRecipe = toRecipe({ ...editingRecipe, ...recipeData });
      } else {
        const userRecipe = await dataService.createUserRecipe(recipeData);
        savedRecipe = toRecipe(userRecipe);
      }

        onSave(savedRecipe);
      } catch (error) {
        alert(`保存失败: ${error instanceof Error ? error.message : '未知错误'}`);
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[#fff8e1] dark:bg-[#3e2723] rounded-lg border-4 border-[#8b4513] dark:border-[#5d4037] shadow-pixel-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black text-[#3e2723] dark:text-[#deb887]">
            {editingRecipe ? '✏️ 编辑食谱' : '🍳 新建食谱'}
          </h2>
          <button
            onClick={onClose}
            className="text-[#3e2723] dark:text-[#deb887] hover:text-[#8b4513] dark:hover:text-[#ffb74d] text-2xl font-black"
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.25fr)_320px] gap-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
            <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887] mb-2">
              食谱名称 *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="例如：宫保鸡丁"
            />
            </div>

            <div>
            <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887] mb-2">
              烹饪时间 *
            </label>
            <Input
              value={formData.time}
              onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
              placeholder="例如：30分钟"
            />
            </div>

            <div>
            <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887] mb-2">
              难度 (1-5星)
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, difficulty: star }))}
                  className={`text-2xl transition-transform hover:scale-110 ${star <= (formData.difficulty || 3) ? 'text-[#ffb74d]' : 'text-gray-300'}`}
                >
                  ★
                </button>
              ))}
            </div>
            </div>

            <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887]">
                卡路里
              </label>
              {liveAutoCalories ? (
                <span className="rounded-full border border-[#ef6c00] bg-[#fff3e0] px-2 py-0.5 text-[11px] font-black text-[#8b4513] dark:border-[#ffcc80] dark:bg-[#4e342e] dark:text-[#ffcc80]">
                  系统估算 {liveAutoCalories} kcal
                </span>
              ) : null}
            </div>
            <Input
              type="number"
              value={formData.manualCalories ?? ''}
              onChange={(e) => {
                const nextValue = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  manualCalories: nextValue ? parseInt(nextValue, 10) || undefined : undefined,
                }));
              }}
              placeholder="建议留空由系统根据食材计算"
            />
            </div>
          </div>

          <div className="rounded-xl border-2 border-[#8b4513] bg-white/70 p-4 dark:border-[#5d4037] dark:bg-[#4e342e]/55">
            <div className="flex items-center justify-between gap-3 mb-3">
              <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887]">
                封面
              </label>
              {formData.image && formData.image !== DEFAULT_RECIPE_IMAGE && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="flex size-8 items-center justify-center rounded-full border-2 border-[#8b4513] bg-[#fff8e1] text-lg font-black text-[#8b4513] transition hover:bg-[#ffe0b2] dark:border-[#d7ccc8] dark:bg-[#4e342e] dark:text-[#deb887]"
                  title="删除封面"
                >
                  ×
                </button>
              )}
            </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleSelectImageFile}
          />
            <div className="mb-3 flex justify-center">
              <img
                src={formData.image || DEFAULT_RECIPE_IMAGE}
                alt="Preview"
                className="aspect-square w-full max-w-[220px] rounded-lg border-2 border-[#8b4513] object-cover dark:border-[#5d4037]"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleOpenImagePicker}
              className="w-full"
            >
              上传封面
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887] mb-2">
            标签
          </label>
          <div className="flex gap-2 mb-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder="输入标签"
              className="flex-1"
            />
            <Button variant="primary" onClick={handleAddTag}>
              添加
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mb-2">
            {PRESET_TAGS.map((tag) => (
              <Button
                key={tag}
                onClick={() => handleToggleTag(tag)}
                variant={formData.tags?.includes(tag) ? 'warning' : 'outline'}
                size="xs"
                className="rounded-full"
              >
                {tag}
              </Button>
            ))}
          </div>

          {formData.tags && formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-[#ffb74d] text-[#3e2723] rounded-full text-sm font-bold border-2 border-[#8b4513] flex items-center gap-1"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-[#3e2723] hover:text-red-600 font-black"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887] mb-2">
            描述
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="介绍一下这道菜..."
            rows={3}
            className="w-full px-3 py-2 bg-white dark:bg-[#4e342e] border-2 border-[#8b4513] dark:border-[#5d4037] rounded text-[#3e2723] dark:text-[#deb887] font-bold focus:outline-none focus:border-[#ffb74d] resize-none"
          />
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2 gap-4">
            <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887]">
              食材清单（可选）
            </label>
          </div>

          <div className="mb-3">
            <Button variant="success" size="sm" onClick={handleAddIngredient}>
              + 添加食材
            </Button>
          </div>

          {((formData.ingredients as RecipeIngredientDraft[]) || []).map((ingredient, index) => {
            const matches = getIngredientMatches(ingredient.name || '');
            const draft = getDraft(index, ingredient.name || '');

            return (
              <div key={index} className="mb-2 rounded-lg border-2 border-[#8b4513] dark:border-[#5d4037] p-3 bg-white/60 dark:bg-[#4e342e]/60">
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-2 items-start">
                  <div className="min-w-0 space-y-2">
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#8b4513] dark:text-[#ffcc80]">
                      食材
                    </div>
                    <Input
                      value={ingredient.name}
                      onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                      placeholder="输入食材名称，优先匹配已有词条"
                      className="min-w-0"
                    />
                    {ingredient.name?.trim() && (
                      <div className="space-y-2">
                        {matches.length > 0 ? (
                          <div className="rounded-md border border-dashed border-[#c08a4a] p-3 bg-[#fff7e8] dark:bg-[#4a3423]">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                              <div className="text-xs font-bold text-[#6d4c41] dark:text-[#d7ccc8]">
                                已找到相近食材，请优先选择已有词条
                              </div>
                              <Button
                                type="button"
                                variant={ingredient.createNewIngredient ? 'warning' : 'outline'}
                                size="xs"
                                className="self-start"
                                onClick={() => handleToggleCreateNewIngredient(index, !ingredient.createNewIngredient)}
                              >
                                {ingredient.createNewIngredient ? '编辑新食材信息' : '仍要添加为新食材'}
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {matches.map((matchedIngredient) => {
                                const inventoryItem = userInventory[matchedIngredient.id];
                                const currentStock = inventoryItem?.currentAmount ?? 0;
                                const unit = inventoryItem?.unit ?? matchedIngredient.unit ?? 'g';
                                return (
                                  <button
                                    key={matchedIngredient.id}
                                    type="button"
                                  onClick={() => handleUseExistingIngredient(index, matchedIngredient)}
                                  className={`px-2 py-1 rounded-full border text-xs font-bold transition-colors ${
                                    ingredient.linkedIngredientId === matchedIngredient.id
                                      ? 'bg-[#ffcc80] border-[#8b4513] text-[#3e2723]'
                                      : 'bg-transparent border-[#8b4513] text-[#5d4037] hover:bg-[#fff3cd]'
                                  }`}
                                >
                                    {matchedIngredient.name}
                                    {ingredient.linkedIngredientId === matchedIngredient.id ? ' · 已选' : ''}
                                    {' '} (库存: {currentStock} {unit})
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-md border border-dashed border-[#c08a4a] p-3 bg-[#fff7e8] dark:bg-[#4a3423]">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                              <div className="text-xs text-[#6d4c41] dark:text-[#d7ccc8]">
                                未找到匹配食材，你可以把它添加为新食材。
                              </div>
                              <Button
                                type="button"
                                variant={ingredient.createNewIngredient ? 'warning' : 'primary'}
                                size="xs"
                                className="self-start"
                                onClick={() => handleToggleCreateNewIngredient(index, !ingredient.createNewIngredient)}
                              >
                                {ingredient.createNewIngredient ? '编辑新食材信息' : '添加为新食材'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="danger"
                    onClick={() => handleRemoveIngredient(index)}
                    className="h-auto md:mt-6"
                  >
                    删除
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,180px)_140px] gap-2 mt-3">
                  <div className="min-w-0">
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#8b4513] dark:text-[#ffcc80]">
                      数量
                    </div>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={ingredient.amountValue || ''}
                      onChange={(e) => handleIngredientChange(index, 'amountValue', e.target.value)}
                      placeholder="例如：200"
                      className="min-w-0"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#8b4513] dark:text-[#ffcc80]">
                      单位
                    </div>
                    <Select
                      value={ingredient.amountUnit || 'g'}
                      onChange={(e) => handleIngredientChange(index, 'amountUnit', e.target.value)}
                      options={[
                        { value: 'g', label: 'g' },
                        { value: 'ml', label: 'ml' },
                      ]}
                    />
                  </div>
                </div>

                <div className="mt-2">
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#8b4513] dark:text-[#ffcc80]">
                    处理说明
                  </div>
                  <Input
                    value={ingredient.note || ''}
                    onChange={(e) => handleIngredientChange(index, 'note', e.target.value)}
                    placeholder="例如：切条后用少许盐腌10分钟"
                    className="min-w-0"
                  />
                </div>

                {!ingredient.linkedIngredientId && ingredient.createNewIngredient && (
                  <div className="rounded-md border border-dashed border-[#8b4513] p-3 bg-[#fff8e1] dark:bg-[#3e2723] mt-3">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                      <div>
                        <div className="text-xs font-bold text-[#8b4513] dark:text-[#ffcc80]">
                          将创建新食材：{draft.name || ingredient.name || '未命名食材'}
                        </div>
                        <div className="text-[11px] text-[#6d4c41] dark:text-[#d7ccc8]">
                          后续可到食材页补充更详细信息，也可提醒站长收录
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs font-bold text-[#8b4513] dark:text-[#ffcc80]">
                          剩余自定义食材: {remainingCustomIngredients} / 100
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          onClick={() => setActiveNewIngredientIndex(index)}
                        >
                          编辑信息
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mb-4">
          <div className="mb-2">
            <label className="block text-sm font-bold text-[#3e2723] dark:text-[#deb887]">
              烹饪步骤
            </label>
          </div>

          <div className="mb-3">
            <Button
              variant="success"
              size="sm"
              onClick={handleAddStep}
            >
              + 添加步骤
            </Button>
          </div>

          {(formData.steps || []).map((step, index) => (
            <div key={index} className="mb-2 rounded-lg border-2 border-[#8b4513] dark:border-[#5d4037] p-3 bg-white/60 dark:bg-[#4e342e]/60">
              <div className="flex flex-col md:flex-row gap-2">
                <div className="flex-shrink-0 w-8 h-8 bg-[#ffb74d] text-[#3e2723] font-black rounded-full flex items-center justify-center border-2 border-[#8b4513]">
                {index + 1}
                </div>
                <textarea
                  value={step}
                  onChange={(e) => handleStepChange(index, e.target.value)}
                  placeholder="步骤描述..."
                  rows={2}
                  className="flex-1 px-3 py-2 bg-white dark:bg-[#4e342e] border-2 border-[#8b4513] dark:border-[#5d4037] rounded text-[#3e2723] dark:text-[#deb887] font-bold focus:outline-none focus:border-[#ffb74d] resize-none min-h-[88px]"
                />
                <Button
                  variant="danger"
                  onClick={() => handleRemoveStep(index)}
                  className="h-auto self-start"
                >
                  删除
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-4 justify-end">
          <Button onClick={onClose} variant="neutral">
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? '保存中...' : editingRecipe ? '更新食谱' : '保存食谱'}
          </Button>
        </div>

        {cropState && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/45 p-4">
            <div className="w-full max-w-3xl rounded-lg border-4 border-[#8b4513] bg-[#fff8e1] p-5 shadow-pixel-lg dark:border-[#5d4037] dark:bg-[#3e2723]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-[#3e2723] dark:text-[#deb887]">
                  裁剪食谱图片
                </h3>
                <button
                  type="button"
                  onClick={() => setCropState(null)}
                  className="text-2xl font-black text-[#3e2723] dark:text-[#deb887]"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-5">
                <div className="rounded-lg border-2 border-[#8b4513] bg-[#f8ecd1] p-4 dark:border-[#5d4037] dark:bg-[#4e342e]">
                  <div
                    className="mx-auto aspect-square max-w-md overflow-hidden rounded-lg border-2 border-[#8b4513] bg-[#f8ecd1] dark:border-[#5d4037]"
                  >
                    <img
                      src={cropState.src}
                      alt="Crop preview"
                      className="h-full w-full object-cover"
                      style={{
                        transform: `translate(${cropState.offsetX}px, ${cropState.offsetY}px) scale(${cropState.scale})`,
                        transformOrigin: 'center center',
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-bold text-[#3e2723] dark:text-[#deb887]">
                      缩放
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.05"
                      value={cropState.scale}
                      onChange={(e) => setCropState(prev => prev ? { ...prev, scale: Number(e.target.value) } : prev)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-bold text-[#3e2723] dark:text-[#deb887]">
                      左右位置
                    </label>
                    <input
                      type="range"
                      min="-240"
                      max="240"
                      step="1"
                      value={cropState.offsetX}
                      onChange={(e) => setCropState(prev => prev ? { ...prev, offsetX: snapToCenter(Number(e.target.value)) } : prev)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-bold text-[#3e2723] dark:text-[#deb887]">
                      上下位置
                    </label>
                    <input
                      type="range"
                      min="-240"
                      max="240"
                      step="1"
                      value={cropState.offsetY}
                      onChange={(e) => setCropState(prev => prev ? { ...prev, offsetY: snapToCenter(Number(e.target.value)) } : prev)}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="neutral"
                      size="sm"
                      onClick={() => setCropState(prev => prev ? { ...prev, offsetX: 0, offsetY: 0, scale: 1 } : prev)}
                    >
                      复位位置
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-3">
                <Button type="button" variant="neutral" onClick={() => setCropState(null)}>
                  取消
                </Button>
                <Button type="button" variant="success" onClick={handleApplyCroppedImage}>
                  应用裁剪
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeNewIngredientIndex !== null && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/45 p-4">
            <div className="w-full max-w-lg rounded-lg border-4 border-[#8b4513] bg-[#fff8e1] p-5 shadow-pixel-lg dark:border-[#5d4037] dark:bg-[#3e2723]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-[#3e2723] dark:text-[#deb887]">
                  添加为新食材
                </h3>
                <button
                  type="button"
                  onClick={() => setActiveNewIngredientIndex(null)}
                  className="text-2xl font-black text-[#3e2723] dark:text-[#deb887]"
                >
                  ✕
                </button>
              </div>

              <div className="mb-3 text-sm font-bold text-[#8b4513] dark:text-[#ffcc80]">
                剩余自定义食材: {remainingCustomIngredients} / 100
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input
                  value={getDraft(activeNewIngredientIndex, (((formData.ingredients as RecipeIngredientDraft[]) || [])[activeNewIngredientIndex]?.name || '')).name}
                  onChange={(e) => handleDraftChange(
                    activeNewIngredientIndex,
                    'name',
                    e.target.value,
                    (((formData.ingredients as RecipeIngredientDraft[]) || [])[activeNewIngredientIndex]?.name || '')
                  )}
                  placeholder="名称"
                />
                <Input
                  value={getDraft(activeNewIngredientIndex, (((formData.ingredients as RecipeIngredientDraft[]) || [])[activeNewIngredientIndex]?.name || '')).tags}
                  onChange={(e) => handleDraftChange(
                    activeNewIngredientIndex,
                    'tags',
                    e.target.value,
                    (((formData.ingredients as RecipeIngredientDraft[]) || [])[activeNewIngredientIndex]?.name || '')
                  )}
                  placeholder="标签，逗号分隔"
                />
              </div>
              <textarea
                value={getDraft(activeNewIngredientIndex, (((formData.ingredients as RecipeIngredientDraft[]) || [])[activeNewIngredientIndex]?.name || '')).description}
                onChange={(e) => handleDraftChange(
                  activeNewIngredientIndex,
                  'description',
                  e.target.value,
                  (((formData.ingredients as RecipeIngredientDraft[]) || [])[activeNewIngredientIndex]?.name || '')
                )}
                placeholder="介绍（可选）"
                rows={3}
                className="w-full mt-3 px-3 py-2 bg-white dark:bg-[#4e342e] border-2 border-[#8b4513] dark:border-[#5d4037] rounded text-[#3e2723] dark:text-[#deb887] font-bold focus:outline-none focus:border-[#ffb74d] resize-none"
              />

              <div className="mt-4 flex justify-end gap-3">
                <Button type="button" variant="neutral" onClick={() => setActiveNewIngredientIndex(null)}>
                  取消
                </Button>
                <Button type="button" variant="success" onClick={() => handleConfirmNewIngredientDraft(activeNewIngredientIndex)}>
                  确认添加
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
